import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Clock, Zap, Star, Navigation, Medal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { GIG_CATEGORIES, GIG_STATUS } from '../lib/gigMockData';
import { resolveAssetUrl } from '../lib/assets';

export default function GigCard({ gig }) {
  const { language } = useLanguage();
  const cat = GIG_CATEGORIES[gig.category];
  const catLabel = gig.category === 'other' && gig.category_other ? gig.category_other : (cat ? cat[language] || cat.lv : gig.category);
  const status = GIG_STATUS[gig.status];

  const formatSchedule = () => {
    if (gig.schedule === 'asap') {
      return language === 'lv' ? 'Tagad / pēc iespējas ātrāk' : language === 'ru' ? 'Сейчас / как можно скорее' : 'ASAP';
    }
    if (!gig.scheduled_at) return null;
    const d = new Date(gig.scheduled_at);
    return d.toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <Link
      to={`/gigs/${gig.id}`}
      className="group block bg-white border border-[#E5E4E0] rounded-xl p-5 hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
      data-testid={`gig-card-${gig.id}`}
    >
      {gig.images?.length > 0 && (
        <div className="-mx-5 -mt-5 mb-4 h-36 overflow-hidden rounded-t-xl bg-[#F3F2EE]">
          <img src={resolveAssetUrl(gig.images[0])} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Badge className="bg-[#F3F2EE] text-[#5C5C5C] border-0 font-figtree">
            {catLabel}
          </Badge>
          {gig.schedule === 'asap' && (
            <span className="inline-flex items-center gap-1 text-[#D97736] text-xs font-semibold">
              <Zap className="w-3.5 h-3.5" /> {language === 'lv' ? 'Steidzami' : language === 'ru' ? 'Срочно' : 'Urgent'}
            </span>
          )}
        </div>
        <Badge className={`${status.color} text-white border-0`} data-testid={`gig-status-${gig.id}`}>
          {status[language] || status.lv}
        </Badge>
      </div>

      <h3 className="font-outfit font-semibold text-[#121212] text-lg mb-2 line-clamp-1 group-hover:text-[#D97736] transition-colors">
        {gig.title}
      </h3>
      <p className="text-[#5C5C5C] text-sm line-clamp-2 mb-4 font-figtree">
        {gig.description}
      </p>

      <div className="flex items-center justify-between mb-3">
        <div className="text-2xl font-bold text-[#121212] font-outfit">
          €{gig.budget}
        </div>
        {gig.distance_km != null && (
          <div className="flex items-center gap-1 text-xs text-[#4A6B53] font-medium">
            <Navigation className="w-3.5 h-3.5" />
            {gig.distance_km} km
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-[#5C5C5C] border-t border-[#F3F2EE] pt-3">
        <div className="flex items-center gap-1 min-w-0">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{gig.city || gig.region}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Clock className="w-3.5 h-3.5" />
          <span>{formatSchedule()}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <div className="w-6 h-6 rounded-full bg-[#D97736] flex items-center justify-center text-white text-[11px] font-medium">
          {(gig.buyer_name || gig.poster_name || '?').charAt(0)}
        </div>
        <span className="text-xs text-[#5C5C5C] truncate">{gig.buyer_name || gig.poster_name}</span>
        {gig.buyer_level_info && (
          <span
            className="inline-flex items-center gap-0.5 rounded-full text-white text-[10px] px-1.5 py-0.5 font-semibold shrink-0"
            style={{ backgroundColor: gig.buyer_level_info.color }}
            data-testid={`gig-buyer-level-${gig.id}`}
            title={gig.buyer_level_info[language] || gig.buyer_level_info.lv}
          >
            <Medal className="w-2.5 h-2.5" />{gig.buyer_level}
          </span>
        )}
        {(gig.buyer_review_count > 0 || gig.poster_rating != null) && (
          <span className="inline-flex items-center gap-0.5 text-xs text-[#5C5C5C] ml-auto shrink-0">
            <Star className="w-3.5 h-3.5 fill-[#D97736] text-[#D97736]" />
            {gig.buyer_avg_rating ?? gig.poster_rating} ({gig.buyer_review_count ?? gig.poster_reviews})
          </span>
        )}
      </div>
    </Link>
  );
}
